import { useMemo } from 'react';
import { type QueryConstraint, type Timestamp, documentId, where } from 'firebase/firestore';
import { useCollection } from './useFirestore';

/**
 * Lista de `medicos` con su latido de presencia ya resuelto en `ultimoHeartbeat`.
 *
 * El latido vive en `medicos_latido/{uid}` (y no en el doc del médico) para que
 * escribirlo cada 45 s no dispare los triggers de `medicos` ni haga releer la
 * lista entera a cada pantalla. Durante la transición, una pestaña con la
 * versión anterior puede seguir escribiendo el campo viejo: se toma el más
 * reciente de los dos.
 *
 * `soloUid` restringe ambas lecturas al propio médico (un médico no puede
 * listar la colección).
 */
export function useMedicosConLatido<T extends { id: string; ultimoHeartbeat?: Timestamp }>(
  constraints?: QueryConstraint[],
  soloUid?: string,
) {
  const latidoConstraints = useMemo(
    () => (soloUid ? [where(documentId(), '==', soloUid)] : undefined),
    [soloUid],
  );
  const medicos = useCollection<T>('medicos', constraints);
  const { data: latidos } = useCollection<{ id: string; ultimoHeartbeat?: Timestamp }>(
    'medicos_latido',
    latidoConstraints,
  );

  const data = useMemo(() => {
    if (!medicos.data) return medicos.data;
    const porUid = new Map((latidos ?? []).map((l) => [l.id, l.ultimoHeartbeat]));
    return medicos.data.map((m) => {
      const nuevo = porUid.get(m.id);
      const msNuevo = nuevo?.toMillis?.() ?? 0;
      const msViejo = m.ultimoHeartbeat?.toMillis?.() ?? 0;
      return msNuevo > msViejo ? { ...m, ultimoHeartbeat: nuevo } : m;
    });
  }, [medicos.data, latidos]);

  return { ...medicos, data };
}
